// @ts-nocheck
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";

const MIN_WIDTH = 300;
const TOP_SHIFT = 0;
const LEFT_SHIFT = -6;
const START_TOP = 160;
const START_LEFT = window.innerWidth > 400 ? 384 : 351;

export const guideSteps = {
  PROFILE: "PROFILE",
  ORGANIZATION_PROFILE: "ORGANIZATION_PROFILE",
  USERS: "USERS",
  PAYMENT: "PAYMENT",
  PRACTICES: "PRACTICES",
  SENDERS: "SENDERS",
  BUTTONS: "BUTTONS",
  ANALYTICS: "ANALYTICS",
};

const firstStep = Object.values(guideSteps)[0];

const resetZIndex = () => {
  const request = Object.keys(guideSteps).map((key) => `.${guideSteps[key]}`);
  const targets = document.querySelectorAll(request?.join(", "));
  targets.forEach((target) => {
    if (target?.style) {
      target.style.zIndex = "";
    }
  });
};

const checkIfElementUnderScroll = (element) => {
  if (!element) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  const viewHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight
  );
  return rect.top > (viewHeight / 4) * 3;
};

const isElementHidden = (element) => {
  if (!element) {
    return true;
  }

  let currentElement = element;
  while (currentElement) {
    const style = window.getComputedStyle(currentElement);
    if (style.display === "none" || style.visibility === "hidden") {
      return true;
    }
    currentElement = currentElement.parentElement;
  }

  return false;
};

const findTargetByStepClass = (stepId) => {
  resetZIndex();
  const targets = document.querySelectorAll(`.${stepId}`);

  let target = [...targets].find((target) => {
    const checkVisibility = target?.checkVisibility1;
    return typeof checkVisibility === "function"
      ? target.checkVisibility()
      : !isElementHidden(target);
  });

  if (!target) {
    target = targets[0];
  }

  if (target?.style) {
    target.style.zIndex = 60;
  }

  window.scrollTo(0, 0);

  if (checkIfElementUnderScroll(target)) {
    target?.scrollIntoView();
  }

  return target;
};

const scrollParentsToTop = (element) => {
  if (!element) {
    return;
  }

  const getScrollableParents = (node) => {
    const parents = [];
    let currentNode = node.parentElement;

    while (currentNode) {
      const overflow = window.getComputedStyle(currentNode).overflow;
      const isScrollable =
        ["auto", "scroll"].includes(overflow) ||
        currentNode.scrollHeight > currentNode.clientHeight;

      if (isScrollable) {
        parents.push(currentNode);
      }
      currentNode = currentNode.parentElement;
    }

    parents.push(window);
    return parents;
  };

  const scrollableParents = getScrollableParents(element);

  scrollableParents.forEach((parent) => {
    if (parent === window) {
      window.scrollTo({
        top: 0,
        behavior: "instant",
      });
    } else {
      parent.scrollTo({
        top: 0,
        behavior: "instant",
      });
    }
  });
};

export const useOnboarding = () => {
  const { userData } = useContext(GlobalContext);

  const defaultIsOnboardingFinished =
    window.localStorage.getItem(
      `${userData?.cognitoId}-is-onboarding-finished`
    ) === "true";

  const [isOnboardingFinished, setIsOnboardingFinished] = useState(
    defaultIsOnboardingFinished
  );
  const [currentStep, setCurrentStep] = useState(firstStep);
  const [onboardingTarget, setOnboardingTarget] = useState(null);
  const [posTop, setPosTop] = useState(0);
  const [posLeft, setPosLeft] = useState(0);

  const finishOnboarding = () => {
    window.localStorage.setItem(
      `${userData?.cognitoId}-is-onboarding-finished`,
      "true"
    );
    setIsOnboardingFinished(true);
    setOnboardingTarget(null);
    window.scrollTo(0, 0);
  };

  const nextStep = () => {
    const steps = Object.keys(guideSteps);
    const currentStepIndex = steps.indexOf(currentStep);

    if (currentStepIndex === steps.length - 1) {
      finishOnboarding();
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
  };

  const prevStep = () => {
    const steps = Object.keys(guideSteps);
    const currentStepIndex = steps.indexOf(currentStep);

    if (currentStepIndex === 0) {
      return;
    }

    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    }
  };

  const currentStepIndex = Object.values(guideSteps).indexOf(currentStep) + 1;
  const stepsLength = Object.values(guideSteps).length;

  const resizeHandler = (onboardingTarget) => {
    const rectBefore = onboardingTarget.getBoundingClientRect();
    const dialog = document.getElementById("onboarding-marker");
    const dialogRect = dialog?.getBoundingClientRect();
    const dialogWidth = dialogRect?.width || START_LEFT;
    const dialogHeight = dialogRect?.height || START_TOP;

    if (onboardingTarget?.tagName === "BODY") {
      setPosTop(window.innerHeight - dialogHeight - 20);
      setPosLeft(window.innerWidth / 2 - dialogWidth / 2);
      return;
    }
    if (onboardingTarget) {
      let top = rectBefore?.top ?? 0;

      if (top < 0) {
        scrollParentsToTop(onboardingTarget);
      }

      const rect = onboardingTarget.getBoundingClientRect();
      top = rect.top + TOP_SHIFT + window.scrollY;
      let left =
        rect.left + rect.width + LEFT_SHIFT + window.scrollX + TOP_SHIFT;
      const windowWidth = window.innerWidth;

      if (windowWidth - left < MIN_WIDTH) {
        top = rect.top + rect.height + window.scrollY + TOP_SHIFT;
        left = rect.left + window.scrollX + LEFT_SHIFT;
      }

      if (top > 0 && left > 0) {
        setPosTop(top);
        setPosLeft(left);
        return;
      }

      setPosTop(window.innerHeight - dialogHeight - 20);
      setPosLeft(window.innerWidth / 2 - dialogWidth / 2);
    }
  };

  useEffect(() => {
    const resize = () => resizeHandler(onboardingTarget);
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [onboardingTarget]);

  useEffect(() => {
    const target = findTargetByStepClass(currentStep);

    if (!target) {
      const body = document.querySelector("body");
      setOnboardingTarget(body);
      resizeHandler(body);
      return;
    }
    setOnboardingTarget(target);
    resizeHandler(target);
  }, [currentStep]);

  useEffect(() => {
    const onboardingMarker = document.getElementById("onboarding-marker");
    if (!onboardingMarker) return;

    if (checkIfElementUnderScroll(onboardingMarker)) {
      window.scrollTo(0, 0);
      onboardingMarker?.scrollIntoView();
    }
  }, [posTop]);

  return {
    isOnboardingFinished: userData?.id || isOnboardingFinished,
    onboardingTarget,
    currentStep,
    nextStep,
    prevStep,
    finishOnboarding,
    posTop,
    posLeft,
    currentStepIndex,
    stepsLength,
  };
};
